import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useRemotionPlayerPause } from '../ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPause';
import { useRemotionPlayerSeek } from '../ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import useRemotionPlayerDuration from '../ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerDuration';
import { useRemotionPlayerCurrentTimeFromContext } from '../ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTimeFromContext';
import PlayPause from '@/components/molecules/PlayPause';
import ProgressBarSection from '@/Pages/Clip/ClipPlayer/ClipPlayerControls/ProgressBarSection';
import { formatTime } from '@/libs/core';
import { useViewportState } from '@/components/atoms/ViewportStateProvider';
import PlaybackSpeedButton from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelinePlayerActionBar/PlaybackSpeedButton';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const ClipPlayerControlsV2 = ({ removeThumbnailImage }: { removeThumbnailImage?: () => void }) => {
  const { isPlaying, play, pause } = useRemotionPlayerPause();

  const { currentTime: currentExactSeconds } = useRemotionPlayerCurrentTimeFromContext();

  const { isInView } = useViewportState();

  const { seekTo } = useRemotionPlayerSeek();

  const currentIntegerSeconds = useMemo(() => {
    return Math.floor(currentExactSeconds);
  }, [currentExactSeconds]);

  const clipDuration = useRemotionPlayerDuration();

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isPlaybackSpeedEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Playback_Speed];

  useEffect(() => {
    if (!isInView && isPlaying) {
      pause();
    }
  }, [isInView, isPlaying, pause]);

  const onSeeked = useCallback(
    (time: number) => {
      removeThumbnailImage?.();
      seekTo(time);
      // if before seeking video was playing, then play after seek as well
      if (isPlaying) {
        play();
      }
    },
    [seekTo, play, isPlaying, removeThumbnailImage]
  );

  const onPlay = () => {
    removeThumbnailImage?.();
    play();
  };

  return (
    <div className="flex w-full items-center justify-center bg-white px-3 py-2">
      <div className="flex w-full max-w-screen-md items-center gap-0.5">
        <PlayPause paused={!isPlaying} onPlay={onPlay} onPause={pause} disabled={false} />
        <span className="w-[38px] select-none text-center text-[10px] text-slate-400">
          {formatTime(currentIntegerSeconds)}
        </span>
        <div className="flex w-full">
          <ProgressBarSection
            section="main"
            duration={clipDuration}
            totalDuration={clipDuration}
            currentTime={currentExactSeconds}
            status={'current'}
            disabled={false}
            onSeeked={onSeeked}
          />
        </div>
        <span className="w-[38px] select-none text-center text-[10px] text-slate-400">{formatTime(clipDuration)}</span>
        {isPlaybackSpeedEnabled && <PlaybackSpeedButton />}
      </div>
    </div>
  );
};

export default ClipPlayerControlsV2;
